﻿import * as Q from "q";
import { IMessage } from "./models";

export enum eScrollMode {
    nearest,
    middleIfNecessary,
}

export class Utils {
    public static readonly MOBILE_WIDTH = 847;

    public static loadScriptAsync(src): Q.Promise<any> {
        let deferred = Q.defer();
        var s, t;
        s = document.createElement('script');
        s.type = 'text/javascript';
        s.src = src;
        s.onload = () => {
            deferred.resolve();
        };
        t = document.getElementsByTagName('script')[0];
        t.parentNode.insertBefore(s, t);
        return deferred.promise;
    }

    public static replaceAll(str: string, find: string, replace: string) {
        if (str) {
            return str.replaceAll(find, replace);
        }
        return str;
    }

    public static scrubName(str: string) {
        if (!str) return "";
        return str.replace(/[^a-z0-9]/gi, "_");
    }
    
    public static guard(condition: boolean, failMessage?: string): void {
        if (!condition) throw (failMessage ? failMessage : "Guard failed");
    }

    public static guardNotNull(variable: any, failMessage?: string): void {
        Utils.guard(
            variable !== null && variable !== undefined,
            (failMessage ? failMessage : "Value must not be null")
        );
    }

    public static guardNotEmpty(variable: string, failMessage?: string): void {
        Utils.guard(
            variable !== null && variable !== undefined && variable.length > 0,
            (failMessage ? failMessage : "String must not be empty")
        );
    }

    public static getParameterByName(url: string, name: string): string {
        name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
        let regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
        let results = regex.exec(url);
        return results == null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
    }

    public static isDefined(o): boolean {
        return typeof o !== "undefined";
    }

    public static extend(...objects: any[]): any {
        let o;
        if (objects && objects.length) {
            o = objects[0];
            for (let i = 1; i < objects.length; i++) {
                let o2 = objects[i];
                for (let prop in o2) {
                    if (o2.hasOwnProperty(prop)) {
                        o[prop] = o2[prop];
                    }
                }
            }

        }
        return o;
    }

    public static getFilenameWithoutExtension(path: string): string {
        let filename = path.substring(path.lastIndexOf("/") + 1);
        let sections = filename.split(".");

        return sections[0];
    }

    public static getExtension(path: string): string {
        let a = document.createElement("a");
        a.href = path;
        // tslint:disable-next-line:no-bitwise
        return a.pathname.substr((~-a.pathname.lastIndexOf(".") >>> 0) + 2);
    }

    static ABS_URL_REGEX = /^(?:[a-z]+:)?\/\//i;
    public static isAbsoluteUrl(url: string): boolean {
        return Utils.ABS_URL_REGEX.test(url);
    }

    static GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    public static isGuid(guid: string): boolean {
        return (Utils.GUID_REGEX.exec(guid) != null);
    }
       
    /**
     * a shorter version of a uuid that is way more unique than we need
     */
    public static shortId(): string {
        let x = 2147483648;
        // added the slice to get us down to 9 characters
        return Math.floor(Math.random() * x).toString(36).slice(0, 3) +
            // tslint:disable-next-line:no-bitwise
            Math.abs(Math.floor(Math.random() * x) ^ (new Date()).getTime()).toString(36);
    }
    
    public static areEqual(str1: string, str2: string) {
        return (str1 ? str1.toString().toUpperCase() : undefined) ===
            (str2 ? str2.toString().toUpperCase() : undefined);
    }

    public static toSafeString(o) {
        return (o === undefined || o === null ? "" : String(o));
    }

    public static supportsFullscreen(): boolean {
        let doc: any = document;
        return !!(
            doc.documentElement.requestFullscreen ||
            doc.documentElement.msRequestFullscreen ||
            doc.documentElement.mozRequestFullScreen ||
            doc.documentElement.webkitRequestFullscreen
        );
    }

    public static toggleFullscreen() {
        let doc: any = document;
        if (!doc.fullscreenElement &&    // alternative standard method
            !doc.mozFullScreenElement &&
            !doc.webkitFullscreenElement &&
            !doc.msFullscreenElement
        ) {  // current working methods
            if (doc.documentElement.requestFullscreen) {
                doc.documentElement.requestFullscreen();
            } else if (doc.documentElement.msRequestFullscreen) {
                doc.documentElement.msRequestFullscreen();
            } else if (doc.documentElement.mozRequestFullScreen) {
                doc.documentElement.mozRequestFullScreen();
            } else if (doc.documentElement.webkitRequestFullscreen) {
                doc.documentElement.webkitRequestFullscreen((Element as any).ALLOW_KEYBOARD_INPUT);
            }
        } else {
            if (doc.exitFullscreen) {
                doc.exitFullscreen();
            } else if (doc.msExitFullscreen) {
                doc.msExitFullscreen();
            } else if (doc.mozCancelFullScreen) {
                doc.mozCancelFullScreen();
            } else if (doc.webkitExitFullscreen) {
                doc.webkitExitFullscreen();
            }
        }
    }

    public static supportsVr(): Q.IPromise<boolean> {
        try {
            if ((window.navigator as any).getVRDisplays) {
                return (window.navigator as any).getVRDisplays().then(displays => {
                    let res = false;
                    if (displays.length) {
                        const userAgent = window.navigator.userAgent;
                        let deviceIncapable = /iPad|iPhone|iPod/.test(userAgent);
                        if (/Android/.test(userAgent)) {
                            deviceIncapable = /^(?!.*Mobile).*$/.test(userAgent);
                        }

                        res = displays[0].capabilities.canPresent && !deviceIncapable;
                    }
                    return res;
                });
            } else {
                return <Q.IPromise<boolean>>Q.when(false);
            }
        } catch (e) {
            return <Q.IPromise<boolean>>Q.when(false);
        }
    }

    private static _isIos: boolean;
    private static _iosChecked: boolean;
    public static isIos(): boolean {
        if (!this._iosChecked) {
            this._isIos = new RegExp("/iPad|iPhone|iPod/").test(navigator.userAgent) && !(window as any).MSStream;
            this._iosChecked = true;
        }
        return this._isIos;
    }

    /**
     * sends a message to the parent page, if the current page is in an iframe
     */
    public static sendMessageToParent(msg: IMessage) {
        //if we are not in an iframe, then window.parent just returns the window we are in
        if (window) {
            window.postMessage(msg, "*");
        }
        if (window.parent && window.parent != window) {
            window.parent.postMessage(msg, "*");
        }
    }

    public static downloadFile(url: string) {
        let ext = Utils.getExtension(url);
        let filename = Utils.getFilenameWithoutExtension(url);
        let link = $("<a/>");
        link.attr("href", url)
            .attr("target", "_blank")
            .attr("download", filename + "." + ext)
        [0].click();
    }

    /**
     * Returns a function, that, as long as it continues to be invoked, will not
     * be triggered. The function will be called after it stops being called for
     * N milliseconds. If `immediate` is passed, trigger the function on the
     * leading edge, instead of the trailing.
     */
    public static debounce(func: Function, wait: number, immediate?: boolean) {
        let timeout;
        return function() {
            let context = this;
            let args = arguments;
            let later = () => {
                timeout = null;
                if (!immediate) func.apply(context, args);
            };
            let callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
            if (callNow) func.apply(context, args);
        };
    }

    /// **
    // * debounces a promise returning function so that at any instant only one call is pending
    // * @param func
    // */
    // public static promiseDebounce(func: Function) {
    //    var pending = null;
    //    function clear() { pending = null; }
    //    return function () {
    //        var ctx = this;
    //        if (pending) return pending;
    //        pending = func.apply(ctx, arguments);
    //        pending.then(clear, clear);
    //        return pending;
    //    }
    // }

    /**
     * throttles a function to only be called at most only once every n milliseconds
     */
    public static throttle(fn: Function, threshhold: number, scope?) {
        let last;
        let deferTimer;
        return function() {
            let context = scope || this;
            let args = arguments;

            if (threshhold <= 0) {
                fn.apply(context, args);
            } else {
                let now = +new Date();
                if (last && now < last + threshhold) {
                    // hold on to it
                    clearTimeout(deferTimer);
                    deferTimer = setTimeout(() => {
                        last = now;
                        fn.apply(context, args);
                    }, threshhold);
                } else {
                    last = now;
                    fn.apply(context, args);
                }
            }
        };
    }

    public static getRelativePos(child: HTMLElement, ancestor: HTMLElement): { left: number, top: number } {
        let left = 0;
        let top = 0;
        let el = child;
        do {
            left += el.offsetLeft;
            top += el.offsetTop;
        } while ((el = el.offsetParent as HTMLElement) && el !== ancestor)

        return { left, top};
    }

    public static escapeMarkup(value: string) {
        var textNode = document.createTextNode(value);
        var div = document.createElement('div');
        div.appendChild(textNode);
        return div.innerHTML;
    }

    public static scrollIntoView(o: { elem: HTMLElement, mode: eScrollMode, animate: boolean, horizontal?: boolean, nearestStartPadding?: number, nearestEndPadding?: number }) {
        if (!o.elem) return;
        let $elem = $(o.elem);
        let $scrollParent = $elem.scrollParent();
        o.nearestStartPadding = o.nearestStartPadding || 0;
        o.nearestEndPadding = o.nearestEndPadding || 0;

        let set = (scrollProp: string, value: number) => {
            if (o.animate) {
                $scrollParent.animate({ [scrollProp]: value }, 150);
            } else {
                $scrollParent[0][scrollProp] = value;
            }
        };

        let elemPos = this.getRelativePos(o.elem, $scrollParent[0]);
        let elemRect = o.elem.getBoundingClientRect();

        if (o.mode == eScrollMode.nearest) {
            if (o.horizontal) {
                let elemLeft = elemPos.left;    
                let elemRight = elemLeft + elemRect.width;
                let scrollLeft = $scrollParent[0].scrollLeft;
                let containerWidth = $scrollParent[0].clientWidth;

                if (elemLeft - scrollLeft < 0) {
                    set("scrollLeft", elemLeft - o.nearestStartPadding);
                } else if (elemRight > scrollLeft + containerWidth) {
                    set("scrollLeft", elemRight - containerWidth + o.nearestEndPadding);
                }
            } else {
                let elemTop = elemPos.top;
                let elemBottom = elemTop + elemRect.height;
                let scrollTop = $scrollParent[0].scrollTop;
                let containerHeight = $scrollParent[0].clientHeight;

                if (elemTop - scrollTop < 0) {
                    set("scrollTop", elemTop - o.nearestStartPadding);
                } else if (elemBottom > scrollTop + containerHeight) {
                    set("scrollTop", elemBottom - containerHeight + o.nearestEndPadding);
                }
            }
        } else if (o.mode == eScrollMode.middleIfNecessary) {
            if (o.horizontal) {
                let elemLeft = elemPos.left;
                let elemWidth = elemRect.width;
                let scrollLeft = $scrollParent[0].scrollLeft;
                let containerWidth = $scrollParent[0].clientWidth;

                if (elemLeft - scrollLeft < 0 || elemLeft + elemWidth > scrollLeft + containerWidth) {
                    let newScrollLeft = elemLeft + (elemWidth / 2) - (containerWidth / 2);
                    //if the elem is taller than the scroll window, we at least want the top visible
                    if (newScrollLeft > elemLeft) newScrollLeft = elemLeft;
                    set("scrollLeft", newScrollLeft);
                }    
            }else{
                let elemTop = elemPos.top;
                let elemHeight = elemRect.height;
                let scrollTop = $scrollParent[0].scrollTop;
                let containerHeight = $scrollParent[0].clientHeight;

                if (elemTop - scrollTop < 0 || elemTop + elemHeight > scrollTop + containerHeight) {
                    let newScrollTop = elemTop + (elemHeight / 2) - (containerHeight / 2);
                    //if the elem is taller than the scroll window, we at least want the top visible
                    if (newScrollTop > elemTop) newScrollTop = elemTop;
                    set("scrollTop", newScrollTop);
                }    
            }    
        }

        // let itemPosition = $item.position();
        // let itemBottom = itemPosition.top + $item.outerHeight(true);
        // let scrollTop = $scrollParent.scrollTop();

        // $scrollParent.scrollTop(scrollTop + itemBottom - $scrollParent.innerHeight());
    }
}
